import type { PayloadAction } from '@reduxjs/toolkit';
import { parseTimeToMinutes } from 'src/components/Scheduling/Timeline/timeUtils';
import { calcDayUtilization, mergeIntervalsMinutes } from 'src/redux/scheduling/normalizer';
import type {
  BlockoutBlock,
  LockedSuggestion,
  SchedulingAlertState,
  SchedulingLocation,
  SchedulingState,
  SchedulingWorkOrder,
  SuggestedWORescheduleSnapshot,
  TimelineView,
  WorkOrderStatus,
} from './types';
import { schedulingInitialState } from './types';

/** Recomputes merged PTO minutes for a technician/date from current state. */
function recomputePTOForDay(
  blockoutBlocksById: Record<string, BlockoutBlock>,
  technicianId: string,
  date: string,
): number {
  const intervals: [number, number][] = [];
  for (const block of Object.values(blockoutBlocksById)) {
    if (block.technicianId !== technicianId || block.date !== date) continue;
    const start = parseTimeToMinutes(block.startTime);
    const end = parseTimeToMinutes(block.endTime);
    if (Number.isFinite(start) && Number.isFinite(end) && end > start) intervals.push([start, end]);
  }
  return mergeIntervalsMinutes(intervals);
}

const reducers = {
  setWorkOrders: (
    state: SchedulingState,
    action: PayloadAction<{ allIds: string[]; byId: Record<string, SchedulingWorkOrder> }>,
  ) => {
    state.workOrders.allIds = action.payload.allIds;
    state.workOrders.byId = action.payload.byId;
  },

  loadSchedulingData: (state: SchedulingState, action: PayloadAction<SchedulingState>) => {
    return { ...state, ...action.payload };
  },

  toggleTechnicianSelection: (state: SchedulingState, action: PayloadAction<string>) => {
    const id = action.payload;
    const idx = state.selectedTechnicianIds.indexOf(id);
    if (idx === -1) {
      state.selectedTechnicianIds.push(id);
    } else {
      state.selectedTechnicianIds.splice(idx, 1);
    }
  },

  deselectAllTechnicians: (state: SchedulingState) => {
    state.selectedTechnicianIds = [];
  },

  selectAllTechniciansAction: (state: SchedulingState) => {
    state.selectedTechnicianIds = state.technicians.allIds.slice();
  },

  toggleMapVisibility: (state: SchedulingState) => {
    state.isMapVisible = !state.isMapVisible;
  },

  toggleWorkOrderLocked: (state: SchedulingState, action: PayloadAction<string>) => {
    const wo = state.workOrders.byId[action.payload];
    if (wo) wo.locked = !wo.locked;
  },

  removeSuggestedWO: (
    state: SchedulingState,
    action: PayloadAction<{ technicianId: string; workOrderId: string }>,
  ) => {
    const { technicianId, workOrderId } = action.payload;
    const technician = state.technicians.byId[technicianId];
    if (!technician) return;

    technician.suggestedWOIds = technician.suggestedWOIds.filter((id) => id !== workOrderId);

    const wo = state.workOrders.byId[workOrderId];
    if (wo?.scheduledDate) {
      const stat = technician.dailyStats[wo.scheduledDate];
      if (stat) {
        const newScheduledMinutes = Math.max(0, stat.totalScheduledMinutes - wo.duration);
        stat.totalScheduledMinutes = newScheduledMinutes;
        stat.utilizationPercentage = calcDayUtilization(
          newScheduledMinutes,
          stat.totalPTOMinutes,
          technician.maxWorkHoursPerDay,
        );
        stat.totalWOs = Math.max(0, stat.totalWOs - 1);
      }
    }

    if (wo?.isReschedule && wo.preOptimizerSchedule) {
      const snap = wo.preOptimizerSchedule;
      wo.technicianId = snap.technicianId;
      wo.scheduledDate = snap.scheduledDate;
      wo.startTime = snap.startTime;
      wo.endTime = snap.endTime;
      wo.startDateTimeUTC = snap.startDateTimeUTC;
      wo.endDateTimeUTC = snap.endDateTimeUTC;
      wo.isReschedule = false;
      wo.preOptimizerSchedule = undefined;
    }

    state.lockedSuggestions = state.lockedSuggestions.filter(
      (s) => s.work_order_uuid !== workOrderId,
    );
  },

  setSelectedDate: (state: SchedulingState, action: PayloadAction<string>) => {
    state.selectedDate = action.payload;
  },

  setTimelineView: (state: SchedulingState, action: PayloadAction<TimelineView>) => {
    state.timelineView = action.payload;
  },

  setWorkOrderAvailabilityRequestPending: (
    state: SchedulingState,
    action: PayloadAction<{ workOrderId: string; pending: boolean }>,
  ) => {
    const wo = state.workOrders.byId[action.payload.workOrderId];
    if (wo) wo.residentAvailabilityRequestPending = action.payload.pending;
  },

  addPendingAvailabilityRequestUuids: (state: SchedulingState, action: PayloadAction<string[]>) => {
    if (action.payload.length === 0) return;
    state.pendingAvailabilityRequestUuids = Array.from(
      new Set([...state.pendingAvailabilityRequestUuids, ...action.payload]),
    );
  },

  setPendingAvailabilityRequestUuids: (state: SchedulingState, action: PayloadAction<string[]>) => {
    state.pendingAvailabilityRequestUuids = Array.from(new Set(action.payload));
  },

  removePendingAvailabilityRequestUuids: (
    state: SchedulingState,
    action: PayloadAction<string[]>,
  ) => {
    const toRemove = new Set(action.payload);
    state.pendingAvailabilityRequestUuids = state.pendingAvailabilityRequestUuids.filter(
      (uuid) => !toRemove.has(uuid),
    );
  },

  clearPendingAvailabilityRequestUuids: (state: SchedulingState) => {
    state.pendingAvailabilityRequestUuids = [];
  },

  applyBulkAvailabilityRequestFailures: (
    state: SchedulingState,
    action: PayloadAction<{ serviceRequestUuids: string[] }>,
  ) => {
    if (action.payload.serviceRequestUuids.length === 0) return;
    const failedSet = new Set(action.payload.serviceRequestUuids);

    for (const workOrderId of state.workOrders.allIds) {
      const workOrder = state.workOrders.byId[workOrderId];
      const serviceRequestUuid = workOrder?.serviceRequestUuid;
      if (serviceRequestUuid && failedSet.has(serviceRequestUuid)) {
        workOrder.residentAvailabilityRequestPending = true;
      }
    }
  },

  setSchedulingWorkOrderStatus: (
    state: SchedulingState,
    action: PayloadAction<{ workOrderId: string; status: WorkOrderStatus }>,
  ) => {
    const wo = state.workOrders.byId[action.payload.workOrderId];
    if (wo) wo.status = action.payload.status;
  },

  setRoutingRequestId: (state: SchedulingState, action: PayloadAction<string | null>) => {
    state.routingRequestId = action.payload;
  },

  setIsOptimizerRunning: (state: SchedulingState, action: PayloadAction<boolean>) => {
    state.isOptimizerRunning = action.payload;
  },

  setHoveredWorkOrderId: (state: SchedulingState, action: PayloadAction<string | null>) => {
    state.hoveredWorkOrderId = action.payload;
  },

  setLockedSuggestions: (state: SchedulingState, action: PayloadAction<LockedSuggestion[]>) => {
    state.lockedSuggestions = action.payload;
  },

  addLockedSuggestion: (state: SchedulingState, action: PayloadAction<LockedSuggestion>) => {
    const exists = state.lockedSuggestions.some(
      (s) => s.work_order_uuid === action.payload.work_order_uuid,
    );
    if (!exists) {
      state.lockedSuggestions.push(action.payload);
    }
  },

  removeLockedSuggestion: (state: SchedulingState, action: PayloadAction<string>) => {
    state.lockedSuggestions = state.lockedSuggestions.filter(
      (s) => s.work_order_uuid !== action.payload,
    );
  },

  resetOptimizer: (state: SchedulingState) => {
    state.isOptimizerRunning = false;
    state.optimizerHasRun = false;
    state.isApplyingSuggestions = false;
    state.routingRequestId = null;
    for (const id of state.workOrders.unscheduledWOIds ?? []) {
      const wo = state.workOrders.byId[id];
      if (wo) wo.unscheduledReasonDetail = undefined;
    }
    state.workOrders.unscheduledWOIds = [];
    state.applyBatchId = null;
    state.optimizerSummary = null;
    state.applyResult = null;
    state.emergencyRescheduleProposals = [];
    state.lockedSuggestions = [];
    state.suggestedWOCount = 0;
    for (const techId of state.technicians.allIds) {
      const tech = state.technicians.byId[techId];
      tech.suggestedWOIds = [];
      if (tech.originalDailyStats) {
        tech.dailyStats = { ...tech.originalDailyStats };
        tech.originalDailyStats = undefined;
      }
    }

    for (const woId of state.workOrders.allIds) {
      const wo = state.workOrders.byId[woId];
      if (wo?.isReschedule && wo.preOptimizerSchedule) {
        const snap = wo.preOptimizerSchedule;
        wo.technicianId = snap.technicianId;
        wo.scheduledDate = snap.scheduledDate;
        wo.startTime = snap.startTime;
        wo.endTime = snap.endTime;
        wo.startDateTimeUTC = snap.startDateTimeUTC;
        wo.endDateTimeUTC = snap.endDateTimeUTC;
        wo.isReschedule = false;
        wo.preOptimizerSchedule = undefined;
      }
    }
  },

  addBlockoutBlock: (state: SchedulingState, action: PayloadAction<BlockoutBlock>) => {
    const block = action.payload;
    state.blockoutBlocks.byId[block.id] = block;
    if (!state.blockoutBlocks.allIds.includes(block.id)) {
      state.blockoutBlocks.allIds.push(block.id);
    }

    const tech = state.technicians.byId[block.technicianId];
    if (tech?.dailyStats[block.date]) {
      const stat = tech.dailyStats[block.date];
      stat.totalPTOMinutes = recomputePTOForDay(
        state.blockoutBlocks.byId,
        block.technicianId,
        block.date,
      );
    }
  },

  updateBlockoutBlock: (state: SchedulingState, action: PayloadAction<BlockoutBlock>) => {
    const newBlock = action.payload;
    const oldBlock = state.blockoutBlocks.byId[newBlock.id];
    if (!oldBlock) return;

    state.blockoutBlocks.byId[newBlock.id] = newBlock;

    // Recompute PTO for every affected date (old and new may differ)
    const datesToUpdate = new Set([oldBlock.date, newBlock.date]);
    const techId = oldBlock.technicianId;
    const tech = state.technicians.byId[techId];
    if (tech) {
      for (const date of datesToUpdate) {
        const stat = tech.dailyStats[date];
        if (stat) {
          stat.totalPTOMinutes = recomputePTOForDay(state.blockoutBlocks.byId, techId, date);
        }
      }
    }
  },

  removeBlockoutBlock: (state: SchedulingState, action: PayloadAction<string>) => {
    const id = action.payload;
    const block = state.blockoutBlocks.byId[id];
    if (!block) return;

    delete state.blockoutBlocks.byId[id];
    state.blockoutBlocks.allIds = state.blockoutBlocks.allIds.filter((bid) => bid !== id);

    const tech = state.technicians.byId[block.technicianId];
    if (tech?.dailyStats[block.date]) {
      // Block already removed from byId so recompute gives the updated value
      tech.dailyStats[block.date].totalPTOMinutes = recomputePTOForDay(
        state.blockoutBlocks.byId,
        block.technicianId,
        block.date,
      );
    }
  },

  addSuggestedWO: (
    state: SchedulingState,
    action: PayloadAction<{
      technicianId: string;
      workOrderId: string;
      rescheduleSnapshot?: SuggestedWORescheduleSnapshot;
    }>,
  ) => {
    const { technicianId, workOrderId, rescheduleSnapshot } = action.payload;
    const technician = state.technicians.byId[technicianId];
    if (!technician) return;
    if (!technician.suggestedWOIds.includes(workOrderId)) {
      technician.suggestedWOIds.push(workOrderId);
    }
    const wo = state.workOrders.byId[workOrderId];
    if (rescheduleSnapshot && wo) {
      wo.technicianId = technicianId;
      wo.scheduledDate = rescheduleSnapshot.scheduledDate;
      wo.startTime = rescheduleSnapshot.startTime;
      wo.endTime = rescheduleSnapshot.endTime;
      wo.startDateTimeUTC = rescheduleSnapshot.startDateTimeUTC;
      wo.endDateTimeUTC = rescheduleSnapshot.endDateTimeUTC;
      wo.isReschedule = true;
      wo.preOptimizerSchedule = rescheduleSnapshot.preOptimizerSchedule;
    }
    if (wo?.scheduledDate) {
      const stat = technician.dailyStats[wo.scheduledDate];
      if (stat) {
        const newScheduledMinutes = stat.totalScheduledMinutes + wo.duration;
        stat.totalScheduledMinutes = newScheduledMinutes;
        stat.utilizationPercentage = calcDayUtilization(
          newScheduledMinutes,
          stat.totalPTOMinutes,
          technician.maxWorkHoursPerDay,
        );
        stat.totalWOs = stat.totalWOs + 1;
      }
    }
  },

  deleteSchedulingWorkOrder: (state: SchedulingState, action: PayloadAction<string>) => {
    const workOrderId = action.payload;
    const wo = state.workOrders.byId[workOrderId];
    if (!wo) return;

    // Don't let the dequeue Pusher delete a mid/just-applied WO during apply (SAAS-1168, COM-325).
    if (
      wo.applyStatus === 'processing' ||
      (state.isApplyingSuggestions &&
        (wo.applyStatus === 'succeeded' || wo.applyStatus === 'skipped'))
    ) {
      return;
    }

    if (wo.technicianId) {
      const technician = state.technicians.byId[wo.technicianId];
      if (technician) {
        technician.suggestedWOIds = technician.suggestedWOIds.filter((id) => id !== workOrderId);
        technician.workOrderIds = technician.workOrderIds.filter((id) => id !== workOrderId);
        if (wo.scheduledDate) {
          const stat = technician.dailyStats[wo.scheduledDate];
          if (stat) {
            const newScheduledMinutes = Math.max(0, stat.totalScheduledMinutes - wo.duration);
            stat.totalScheduledMinutes = newScheduledMinutes;
            stat.utilizationPercentage = calcDayUtilization(
              newScheduledMinutes,
              stat.totalPTOMinutes,
              technician.maxWorkHoursPerDay,
            );
            stat.totalWOs = Math.max(0, stat.totalWOs - 1);
          }
        }
      }
    }

    state.lockedSuggestions = state.lockedSuggestions.filter(
      (s) => s.work_order_uuid !== workOrderId,
    );
    state.workOrders.unscheduledWOIds = (state.workOrders.unscheduledWOIds ?? []).filter(
      (id) => id !== workOrderId,
    );
    delete state.workOrders.byId[workOrderId];
    state.workOrders.allIds = state.workOrders.allIds.filter((id) => id !== workOrderId);
  },

  addUrgentWorkOrder: (
    state: SchedulingState,
    action: PayloadAction<{ workOrder: SchedulingWorkOrder; location: SchedulingLocation }>,
  ) => {
    const { workOrder, location } = action.payload;
    if (state.workOrders.byId[workOrder.id]) return;
    state.workOrders.byId[workOrder.id] = workOrder;
    state.workOrders.allIds.push(workOrder.id);
    if (!state.locations.byId[location.id]) {
      state.locations.byId[location.id] = location;
      state.locations.allIds.push(location.id);
    }
  },

  showSchedulingAlert: (
    state: SchedulingState,
    action: PayloadAction<NonNullable<SchedulingAlertState>>,
  ) => {
    state.schedulingAlert = action.payload;
  },

  clearSchedulingAlert: (state: SchedulingState) => {
    state.schedulingAlert = null;
  },

  updateSchedulingWorkOrderPriority: (
    state: SchedulingState,
    action: PayloadAction<{ workOrderId: string; priority: string }>,
  ) => {
    const wo = state.workOrders.byId[action.payload.workOrderId];
    if (wo) wo.priority = action.payload.priority as SchedulingWorkOrder['priority'];
  },

  updateSchedulingWorkOrderTrip: (
    state: SchedulingState,
    action: PayloadAction<{
      workOrderId: string;
      newTechnicianId: string;
      scheduledDate: string;
      startTime: string;
      endTime: string;
      startDateTimeUTC: string;
      endDateTimeUTC: string;
    }>,
  ) => {
    const { workOrderId, newTechnicianId, scheduledDate, startTime, endTime,
            startDateTimeUTC, endDateTimeUTC } = action.payload;

    const wo = state.workOrders.byId[workOrderId];
    if (!wo) return;

    const oldTechnicianId = wo.technicianId;
    const oldScheduledDate = wo.scheduledDate;

    if (oldTechnicianId && oldTechnicianId !== newTechnicianId) {
      const oldTech = state.technicians.byId[oldTechnicianId];
      if (oldTech) {
        oldTech.workOrderIds = oldTech.workOrderIds.filter((id) => id !== workOrderId);
        if (oldScheduledDate) {
          const stat = oldTech.dailyStats[oldScheduledDate];
          if (stat) {
            const updatedMinutes = Math.max(0, stat.totalScheduledMinutes - wo.duration);
            stat.totalScheduledMinutes = updatedMinutes;
            stat.utilizationPercentage = calcDayUtilization(updatedMinutes, stat.totalPTOMinutes, oldTech.maxWorkHoursPerDay);
            stat.totalWOs = Math.max(0, stat.totalWOs - 1);
          }
        }
      }
      const newTech = state.technicians.byId[newTechnicianId];
      if (newTech && !newTech.workOrderIds.includes(workOrderId)) {
        newTech.workOrderIds.push(workOrderId);
        const stat = newTech.dailyStats[scheduledDate];
        if (stat) {
          const updatedMinutes = stat.totalScheduledMinutes + wo.duration;
          stat.totalScheduledMinutes = updatedMinutes;
          stat.utilizationPercentage = calcDayUtilization(updatedMinutes, stat.totalPTOMinutes, newTech.maxWorkHoursPerDay);
          stat.totalWOs = stat.totalWOs + 1;
        }
      }
    } else if (oldScheduledDate && oldScheduledDate !== scheduledDate) {
      // Same technician, different date — update dailyStats for both days
      const tech = state.technicians.byId[newTechnicianId];
      if (tech) {
        const oldStat = tech.dailyStats[oldScheduledDate];
        if (oldStat) {
          const updatedMinutes = Math.max(0, oldStat.totalScheduledMinutes - wo.duration);
          oldStat.totalScheduledMinutes = updatedMinutes;
          oldStat.utilizationPercentage = calcDayUtilization(updatedMinutes, oldStat.totalPTOMinutes, tech.maxWorkHoursPerDay);
          oldStat.totalWOs = Math.max(0, oldStat.totalWOs - 1);
        }
        const newStat = tech.dailyStats[scheduledDate];
        if (newStat) {
          const updatedMinutes = newStat.totalScheduledMinutes + wo.duration;
          newStat.totalScheduledMinutes = updatedMinutes;
          newStat.utilizationPercentage = calcDayUtilization(updatedMinutes, newStat.totalPTOMinutes, tech.maxWorkHoursPerDay);
          newStat.totalWOs = newStat.totalWOs + 1;
        }
      }
    }

    wo.isReschedule = false;
    wo.technicianId = newTechnicianId;
    wo.scheduledDate = scheduledDate;
    wo.startTime = startTime;
    wo.endTime = endTime;
    wo.startDateTimeUTC = startDateTimeUTC;
    wo.endDateTimeUTC = endDateTimeUTC;
    wo.travelTimeMinutes = null;
  },

  dismissEmergencyRescheduleProposal: (state: SchedulingState, action: PayloadAction<string>) => {
    state.emergencyRescheduleProposals = state.emergencyRescheduleProposals.filter(
      (proposal) => proposal.id !== action.payload,
    );
  },

  approveEmergencyRescheduleProposal: (state: SchedulingState, action: PayloadAction<string>) => {
    const proposalId = action.payload;
    const proposalIndex = state.emergencyRescheduleProposals.findIndex(
      (proposal) => proposal.id === proposalId,
    );
    if (proposalIndex === -1) return;
    const proposal = state.emergencyRescheduleProposals[proposalIndex];

    // Add the emergency WO to its technician's suggested list and update its schedule
    const emergencyWO = state.workOrders.byId[proposal.id];
    if (emergencyWO) {
      emergencyWO.technicianId = proposal.technicianId;
      emergencyWO.scheduledDate = proposal.scheduledDate;
      emergencyWO.startTime = proposal.startTime;
      emergencyWO.endTime = proposal.endTime;
      emergencyWO.startDateTimeUTC = proposal.startDateTimeUTC;
      emergencyWO.endDateTimeUTC = proposal.endDateTimeUTC;
      emergencyWO.travelTimeMinutes = proposal.travelTimeMinutes;
      emergencyWO.locked = false;
      emergencyWO.applyStatus = undefined;
    }
    const emergencyTech = state.technicians.byId[proposal.technicianId];
    if (emergencyTech && !emergencyTech.suggestedWOIds.includes(proposal.id)) {
      emergencyTech.suggestedWOIds.push(proposal.id);
      if (emergencyWO?.scheduledDate) {
        const stat = emergencyTech.dailyStats[emergencyWO.scheduledDate];
        if (stat) {
          const newScheduledMinutes = stat.totalScheduledMinutes + emergencyWO.duration;
          stat.totalScheduledMinutes = newScheduledMinutes;
          stat.utilizationPercentage = calcDayUtilization(
            newScheduledMinutes,
            stat.totalPTOMinutes,
            emergencyTech.maxWorkHoursPerDay,
          );
          stat.totalWOs = stat.totalWOs + 1;
        }
      }
    }

    // Add each displaced WO as a suggested reschedule on the timeline
    for (const affectedWO of proposal.affectedWorkOrders) {
      const displacedWO = state.workOrders.byId[affectedWO.id];
      if (displacedWO) {
        displacedWO.isReschedule = true;
        displacedWO.preOptimizerSchedule = {
          technicianId: affectedWO.oldTechnicianId,
          technicianName: affectedWO.oldTechnicianName,
          scheduledDate: affectedWO.oldScheduledDate,
          startTime: affectedWO.oldStartTime,
          endTime: affectedWO.oldEndTime,
          startDateTimeUTC: affectedWO.oldStartUTC,
          endDateTimeUTC: affectedWO.oldEndUTC,
        };
        displacedWO.technicianId = affectedWO.newTechnicianId;
        displacedWO.scheduledDate = affectedWO.suggestedDate;
        displacedWO.startTime = affectedWO.suggestedStartTime;
        displacedWO.endTime = affectedWO.suggestedEndTime;
        displacedWO.startDateTimeUTC = affectedWO.suggestedStartUTC;
        displacedWO.endDateTimeUTC = affectedWO.suggestedEndUTC;
        displacedWO.travelTimeMinutes = affectedWO.travelTimeMinutes;
        displacedWO.locked = false;
        displacedWO.applyStatus = undefined;
      }
      const newTech = state.technicians.byId[affectedWO.newTechnicianId];
      if (newTech && !newTech.suggestedWOIds.includes(affectedWO.id)) {
        newTech.suggestedWOIds.push(affectedWO.id);
        if (displacedWO?.scheduledDate) {
          const stat = newTech.dailyStats[displacedWO.scheduledDate];
          if (stat) {
            const newScheduledMinutes = stat.totalScheduledMinutes + displacedWO.duration;
            stat.totalScheduledMinutes = newScheduledMinutes;
            stat.utilizationPercentage = calcDayUtilization(
              newScheduledMinutes,
              stat.totalPTOMinutes,
              newTech.maxWorkHoursPerDay,
            );
            stat.totalWOs = stat.totalWOs + 1;
          }
        }
      }
    }

    state.emergencyRescheduleProposals.splice(proposalIndex, 1);
  },

  setSchedulingTimezone: (state: SchedulingState, action: PayloadAction<string>) => {
    state.timezone = action.payload;
  },

  resetSchedulingState: (state: SchedulingState) => {
    return {
      ...schedulingInitialState,
      selectedDate: state.selectedDate,
      timelineView: state.timelineView,
      timezone: state.timezone,
    };
  },
};

export default reducers;
